import { Component, OnInit } from '@angular/core';
import { AppMainComponent } from '../../app.main.component';
import { PERM } from 'src/app/shared/permissions/perm.enum';
import { AuthService } from 'src/app/services/auth/auth.service';
import { Subscription } from 'rxjs';
import {
  PubSubEvent,
  PubSubService,
} from 'src/app/services/util/pub-sub.service';
import { MenuItem } from 'primeng/api';
import { BuscarParametroPorTipoGQL } from 'src/app/generated/graphql';
import { AppMenuitemComponent } from './app.menuitem.component';

interface CustomMenuItem extends MenuItem {
  perms?: string[];
  items?: CustomMenuItem[];
  visible?: boolean;
  param?: string;
}

@Component({
  selector: 'app-menu',
  template: `
    <ul class="layout-menu">
      @for (item of model; track item; let i = $index) {
        <li app-menuitem [item]="item" [index]="i" [root]="true"></li>
      }
    </ul>
  `,
  standalone: true,
  imports: [AppMenuitemComponent],
})
export class AppMenuComponent implements OnInit {
  model: CustomMenuItem[] = [];
  perms: string[] = [];
  eventSubscription: Subscription | undefined;

  constructor(
    public app: AppMainComponent,
    private authService: AuthService,
    private pubSubService: PubSubService,
    private valorParametroPorTipo: BuscarParametroPorTipoGQL,
  ) {
    const { permissoesGrupo, permissoes } = this.authService.getUser() ?? {};
    this.perms = permissoesGrupo?.length ? permissoesGrupo : (permissoes ?? []);
  }

  ngOnInit() {
    this.loadMenuItems();

    this.eventSubscription = this.pubSubService
      .getEvent()
      .subscribe((event: PubSubEvent) => {
        if (event === 'ATUALIZAR_USUARIO_LOGADO') {
          const { permissoesGrupo, permissoes } =
            this.authService.getUser() ?? {};
          this.perms = permissoesGrupo?.length
            ? permissoesGrupo
            : (permissoes ?? []);
          this.loadMenuItems();
        }
      });
  }

  applyPermissions(items: CustomMenuItem[]): CustomMenuItem[] {
    items.forEach((item) => {
      if (item.param) {
        (async () => {
          const visible = await this.checkParamAsync(item.param!);
          item.visible = visible;
        })();
      } else {
        if (
          item.perms &&
          item.perms.length > 0 &&
          !item.perms.some((permission) => this.hasPermission(permission))
        ) {
          item.visible = false;
        }
        if (item.items) {
          const hasVisibleChild = item.items.some((childItem) =>
            this.hasSomePermissionInTheBlock(childItem),
          );
          item.visible = hasVisibleChild;
          item.items = this.applyPermissions(item.items);
        }
      }
    });

    return items;
  }

  checkParamAsync(param: string) {
    return new Promise<boolean>((resolve) => {
      this.valorParametroPorTipo
        .fetch({
          tipo: param,
        })
        .subscribe({
          next: (rs) => {
            const data = rs.data.buscarParametroPorTipo;
            if (data?.valor === 'N') {
              resolve(false);
            } else {
              resolve(true);
            }
          },
        });
    });
  }

  hasSomePermissionInTheBlock(item: CustomMenuItem): boolean {
    if (item.perms?.length === 0) {
      return true;
    }

    if (item.perms && item.perms.length > 0) {
      return item.perms.some((permission) => this.hasPermission(permission));
    }

    return false;
  }

  hasPermission(permission: string): boolean {
    return this.perms.includes(permission);
  }

  loadMenuItems() {
    const models = [
      { label: 'Dashboard', icon: 'pi pi-fw pi-home', routerLink: ['/'] },
      {
        label: 'Administração',
        icon: 'pi pi-briefcase',
        items: [
          {
            label: 'Configurações do Sistema',
            icon: 'pi pi-fw pi-cog',
            routerLink: ['administracao/configuracoes'],
            perms: [PERM.ConfiguracoesDoSistema.Visualizar],
          },
          {
            label: 'Log de Dispositivos',
            icon: 'pi pi-chart-line',
            routerLink: ['administracao/log-dispositivo'],
            perms: [PERM.Logs.VisualizarLogsDeDispositivos],
          },
          {
            label: 'Log do Site',
            icon: 'pi pi-chart-bar',
            routerLink: ['administracao/log-site'],
            perms: [PERM.Logs.VisualizarLogsDoSite],
          },
          {
            label: 'Login do Pessoal de Campo',
            icon: 'pi pi-fw pi-user',
            routerLink: ['administracao/log-agente'],
            perms: [PERM.Logs.VisualizarLogsDePessoalDeCampo],
          },
        ],
      },
      {
        label: 'Cadastros',
        icon: 'pi pi-fw pi-pencil',
        items: [
          {
            label: 'Dispositivos',
            icon: 'pi pi-fw pi-ticket',
            routerLink: ['dispositivo'],
            perms: [PERM.Dispositivo.Visualizar],
          },
          {
            label: 'Usuário Retaguarda',
            icon: 'pi pi-fw pi-user',
            routerLink: ['usuario/retaguarda'],
            perms: [PERM.Usuario.Visualizar],
          },
          {
            label: 'Usuário Dispositivo',
            icon: 'pi pi-fw pi-user',
            routerLink: ['usuario/dispositivo'],
            perms: [PERM.Usuario.Visualizar],
          },
          {
            label: 'Permissões',
            icon: 'pi pi-fw pi-lock',
            routerLink: ['permissao'],
            perms: [PERM.Permissao.Visualizar],
          },
        ],
      },
      {
        label: 'Tabelas',
        icon: 'pi pi-fw pi-table',
        visible: true,
        items: [
          {
            label: 'Marca',
            icon: 'pi pi-fw pi-tag',
            routerLink: ['tabelas/marca'],
            perms: [PERM.Marca.Visualizar],
          },
          {
            label: 'Modelo',
            icon: 'pi pi-fw pi-file',
            routerLink: ['tabelas/modelo'],
            perms: [PERM.Modelo.Visualizar],
          },
          {
            label: 'Rota',
            icon: 'pi pi-fw pi-map-marker',
            routerLink: ['tabelas/rota'],
            perms: [PERM.Rota.Visualizar],
          },
          {
            label: 'Motivo de Ausência',
            icon: 'pi pi-fw pi-ban',
            routerLink: ['tabelas/motivo-ausencia'],
            perms: [PERM.MotivoAusencia.Visualizar],
          },
          {
            label: 'Cidade',
            icon: 'pi pi-fw pi-building',
            routerLink: ['tabelas/cidade'],
            perms: [PERM.Cidade.Visualizar],
          },
          {
            label: 'Regional',
            icon: 'pi pi-fw pi-map',
            routerLink: ['tabelas/regional'],
            perms: [PERM.Regional.Visualizar],
          },
          {
            label: 'Tipo de Captura',
            icon: 'pi pi-fw pi-tags',
            routerLink: ['tabelas/tipo-captura'],
            perms: [PERM.TipoDeCaptura.Visualizar],
          },
        ],
      },
      {
        label: 'Contrato',
        icon: 'pi pi-fw pi-book',
        routerLink: ['contrato'],
        perms: [PERM.Contrato.Visualizar],
      },
      {
        label: 'Contrato ZIP',
        icon: 'pi pi-fw pi-folder',
        routerLink: ['contrato-zip'],
        perms: [PERM.ContratoZip.Visualizar],
      },
      {
        label: 'Faixa',
        icon: 'pi pi-fw pi-camera',
        routerLink: ['faixa'],
        perms: [PERM.Faixa.Visualizar],
      },
      {
        label: 'Solicitação',
        icon: 'pi pi-fw pi-file-edit',
        routerLink: ['solicitacao'],
        perms: [PERM.Solicitacao.Visualizar],
      },
      {
        label: 'Programação',
        icon: 'pi pi-fw pi-clock',
        routerLink: ['programacao'],
        perms: [PERM.Programacao.Visualizar],
      },
      {
        label: 'Registro',
        icon: 'pi pi-fw pi-image',
        routerLink: ['registro'],
        perms: [PERM.Registro.Visualizar],
      },
      {
        label: 'Relatórios',
        icon: 'pi pi-fw pi-file-pdf',
        items: [
          {
            label: 'Relatório de Fotos',
            icon: 'pi pi-fw pi-image',
            routerLink: ['relatorio/fotos'],
            perms: [PERM.Relatorios.Visualizar],
          },
          {
            label: 'Relatório de Faixas',
            icon: 'pi pi-fw pi-camera',
            routerLink: ['relatorio'],
            perms: [PERM.Relatorios.Visualizar],
          },
        ],
      },
    ];

    this.model = this.applyPermissions(models);
  }
}
